import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CloudQueue as CloudIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsOverview: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'overview' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load DevSecOps components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (err) {
        // Swallow errors here; page should still render read-only for unauthenticated users
        console.warn('Unable to load current user for DevSecOps overview:', err);
      }
    };

    loadUser();
  }, []);

  const opportunityDetails = [
    { label: 'Requirement', value: 'ODOS IV (F2024066162)' },
    { label: 'Agency', value: 'USCIS / OIT / TDD' },
    { label: 'NAICS', value: '541512 – Computer Systems Design' },
    { label: 'Set-Aside', value: 'Full Small Business' },
    { label: 'Contract Type', value: 'Hybrid FFP + Labor Hour' },
    { label: 'Status', value: 'Recompetition of ODOS III' },
    { label: 'Est. Solicitation', value: '15 Dec 2025' },
    { label: 'Anticipated Award', value: 'Q2 FY2026' },
    { label: 'Period of Performance', value: 'Through 24 Mar 2031' },
    { label: 'Est. Value', value: 'Over $100M' },
    { label: 'Place of Performance', value: 'Camp Springs, MD' }
  ];

  const missionPriorities = [
    'Proven DevSecOps and automation-by-default',
    'Live/technical demonstrations',
    'Mature CI/CD, monitoring, and shift-left security',
    'Stable, high-performing delivery teams with low key-personnel turnover'
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            DevSecOps Program Overview
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            ODOS IV – Outcome-Based Delivery and DevOps Services
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="overview" />

      {/* Mission Statement */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Mission
          </Typography>
          <Typography variant="body1" paragraph>
            ODOS IV is the <strong>primary contract for USCIS TDD</strong> to obtain DevSecOps, UX, infrastructure,
            and integration support for the continued development and operation of:
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              • The <strong>Electronic Immigration System (ELIS)</strong>
            </Typography>
            <Typography variant="body1">
              • <strong>Global</strong> enterprise case management systems and related platforms
            </Typography>
          </Box>
          <Typography variant="body1">
            ODOS IV will be awarded through a <strong>multi-phase, highly selective down-select</strong>, emphasizing:
          </Typography>
          <List dense>
            {missionPriorities.map((priority, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={priority} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Opportunity Snapshot */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Opportunity Snapshot
          </Typography>
          <Grid container spacing={2}>
            {opportunityDetails.map((detail, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {detail.label}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {detail.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Key Capabilities (from API-backed components) */}
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
        Key DevSecOps Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading DevSecOps overview components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Task Areas */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            ODOS IV Task Areas
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            Task Area 1 – DevSecOps and UXD / IxD Services
          </Typography>
          <Typography variant="body2" paragraph>
            Continue the development, delivery, and support of ELIS and Global. Integrate <strong>technical delivery</strong>,
            <strong> business integration</strong>, <strong>UXD/IxD</strong>, and <strong>adaptive maintenance</strong>.
            Deliver features at the current cadence, <strong>on-time and without disruption</strong>, while improving
            reliability and user experience.
          </Typography>

          <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            Task Area 2 – Infrastructure and Integration Services
          </Typography>
          <Typography variant="body2" paragraph>
            Ensure <strong>continuous software quality</strong> and reduce release time. Improve <strong>monitoring,
            logging, and alerting</strong> for running systems. Adopt a <strong>shift-left security</strong> posture.
            Equip development teams with tools for automated code promotion across environments with <strong>zero downtime</strong>
            and deploying code <strong>multiple times per day</strong> with confidence.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsOverview;
